import { listItem } from './types';

// ref: https://github.com/ckeditor/ckeditor5/blob/c204b2d7230020b17a3386d0390262e77d71903c/packages/ckeditor5-paste-from-office/src/filters/list.js
export const transformListItemLikeElementsIntoLists = (htmlDoc: HTMLDocument) => {
  const itemLikeElements = findAllItemLikeElements(htmlDoc);
  const stylesString = htmlDoc.head.querySelector('style')?.innerHTML;
  if (!itemLikeElements.length) {
    return;
  }

  let currentList: any = null;
  let currentIndentation = 1;

  itemLikeElements.forEach((itemLikeElement, i) => {
    const isDifferentList = isNewListNeeded(itemLikeElements[i - 1], itemLikeElement);
    const previousItemLikeElement = isDifferentList ? null : itemLikeElements[i - 1];
    const indentationDifference = getIndentationDifference(
      itemLikeElement,
      previousItemLikeElement!,
    );

    if (isDifferentList) {
      currentList = null;
      currentIndentation = 1;
    }

    if (!currentList || indentationDifference !== 0) {
      const listStyle = detectListStyle(itemLikeElement, stylesString!);
      if (!currentList) {
        currentList = insertNewEmptyList(listStyle, itemLikeElement.element);
      } else if (itemLikeElement.indent > currentIndentation) {
        const lastListItem = currentList.children[currentList.childElementCount - 1];

        currentList = insertNewEmptyList(listStyle, lastListItem);
        currentIndentation += 1;
      } else if (itemLikeElement.indent < currentIndentation) {
        const differentIndentation = currentIndentation - itemLikeElement.indent;

        currentList = findParentListAtLevel(currentList, differentIndentation);
        currentIndentation = parseInt(itemLikeElement.indent, 10);
      }

      if (
        itemLikeElement.indent <= currentIndentation &&
        currentList.tagName.toLocaleLowerCase() !== listStyle.type
      ) {
        const newList = document.createElement(listStyle.type);
        for (const child of currentList.children) {
          newList.appendChild(child);
        }
        currentList.parentElement.insertBefore(newList, currentList);
        currentList.remove();
        currentList = newList;
      }
    }
    currentList.appendChild(transformElementIntoListItem(itemLikeElement.element));
  });
};

function findAllItemLikeElements(htmlDoc: HTMLDocument) {
  const itemLikeElements = [];

  // @ts-ignore
  for (const child of htmlDoc.body.children) {
    const childStyle = getStyleObject(child.getAttribute('style')!)['mso-list'];
    if (childStyle) {
      const itemData = getListItemData(childStyle);

      itemLikeElements.push({
        element: child as HTMLElement,
        id: itemData.id,
        order: itemData.order,
        indent: itemData.indent,
      });
    }
  }

  return itemLikeElements;
}

const getStyleObject = (style: string) => {
  if (!style) {
    return {};
  }
  let result: any = {},
    attributes = style.split(';');

  for (var i = 0; i < attributes.length; i++) {
    let entry = attributes[i].split(':');
    result[entry.splice(0, 1)[0]] = entry.join(':');
  }
  return result;
};

const getListItemData = (listStyle: string) => {
  let data = {
    id: null,
    order: null,
    indent: null,
  } as any;
  if (listStyle) {
    const idMatch = listStyle.match(/(^|\s{1,100})l(\d+)/i);
    const orderMatch = listStyle.match(/\s{0,100}lfo(\d+)/i);
    const indentMatch = listStyle.match(/\s{0,100}level(\d+)/i);

    if (idMatch && orderMatch && indentMatch) {
      data.id = idMatch[2];
      data.order = orderMatch[1];
      data.indent = indentMatch[1];
    }
  }
  return data;
};

function detectListStyle(listLikeItem: listItem, stylesString: string) {
  const listStyleRegexp = new RegExp(
    `@list l${listLikeItem.id}:level${listLikeItem.indent}\\s*({[^}]*)`,
    'gi',
  );
  const listStyleTypeRegex = /mso-level-number-format:([^;]{0,100});/gi;

  const listStyleMatch = listStyleRegexp.exec(stylesString);

  let listStyleType = 'decimal'; // Decimal is default one.
  let type = 'ol'; // <ol> is default list.

  if (listStyleMatch && listStyleMatch[1]) {
    const listStyleTypeMatch = listStyleTypeRegex.exec(listStyleMatch[1]);

    if (listStyleTypeMatch && listStyleTypeMatch[1]) {
      listStyleType = listStyleTypeMatch[1].trim();
      type = listStyleType !== 'bullet' && listStyleType !== 'image' ? 'ol' : 'ul';
    }

    // Styles for the numbered lists are always defined in the Word CSS stylesheet.
    // Unordered lists MAY contain a value for the Word CSS definition `mso-level-text` but sometimes
    // this tag is missing. And because of that, we cannot depend on that. We need to predict the list style value
    // based on the list style marker element.
    if (listStyleType === 'bullet') {
      const bulletedStyle = findBulletedListStyle(listLikeItem.element);

      if (bulletedStyle) {
        listStyleType = bulletedStyle;
      }
    }
  }

  return {
    type,
    style: mapListStyleDefinition(listStyleType),
  };
}

function findBulletedListStyle(element: HTMLElement) {
  const listMarkerElement = findListMarkerNode(element);

  if (!listMarkerElement) {
    return null;
  }

  const listMarker = listMarkerElement.textContent;

  if (listMarker === 'o') {
    return 'circle';
  } else if (listMarker === '·') {
    return 'disc';
  }
  // Word returns '§' instead of '■' for the square list style.
  else if (listMarker === '§') {
    return 'square';
  }

  return null;
}

function findListMarkerNode(element: HTMLElement) {
  // If the first child is a text node, it is a value for the element.
  if (!element.children[0]) {
    return null;
  }

  const textNodeOrElement = element.children[0].children[0];

  if (!textNodeOrElement) {
    return element.children[0];
  }

  return textNodeOrElement;
}

function mapListStyleDefinition(value: string) {
  switch (value) {
    case 'arabic-leading-zero':
      return 'decimal-leading-zero';
    case 'alpha-upper':
      return 'upper-alpha';
    case 'alpha-lower':
      return 'lower-alpha';
    case 'roman-upper':
      return 'upper-roman';
    case 'roman-lower':
      return 'lower-roman';
    case 'circle':
    case 'disc':
    case 'square':
      return value;
    default:
      return '';
  }
}

function insertNewEmptyList(listStyle: { type: string; style: string }, element: HTMLElement) {
  const parent = element.parentElement!;
  const list = document.createElement(listStyle.type);
  if (isList(parent)) {
    parent.appendChild(list);
  } else {
    parent.insertBefore(list, element);
  }

  // We do not support modifying the marker for a particular list item.
  // Set the value for the `list-style-type` property directly to the list container.
  if (listStyle.style) {
    list.style.listStyleType = listStyle.style;
  }

  return list;
}

const isList = (element: HTMLElement) => {
  return element.tagName === 'OL' || element.tagName === 'UL';
};

const transformElementIntoListItem = (element: HTMLElement) => {
  removeBulletElement(element);
  let listElement = document.createElement('li');
  listElement.textContent = element.textContent;
  element.parentNode?.insertBefore(listElement, element);
  element.remove();
  return listElement;
};

const removeBulletElement = (element: HTMLElement) => {
  // @ts-ignore
  const childArray = [...element.children];
  for (const child of childArray) {
    if (
      child.tagName === 'SPAN' &&
      child.getAttribute('style') &&
      getStyleObject(child.getAttribute('style')!)['mso-list'] === 'Ignore'
    ) {
      child.remove();
    }
    childArray.push(...child.children);
  }
};

const isNewListNeeded = (previousItem: listItem, currentItem: listItem) => {
  if (!previousItem) {
    return true;
  }

  if (previousItem.id !== currentItem.id) {
    // See: https://github.com/ckeditor/ckeditor5/issues/7805.
    //
    // * List item 1.
    //     - Nested list item 1.
    if (currentItem.indent - previousItem.indent === 1) {
      return false;
    }

    return true;
  }

  const previousSibling = currentItem.element.previousSibling;

  if (!previousSibling) {
    return true;
  }

  // Even with the same id the list does not have to be continuous (#43).
  return !isList(previousSibling as HTMLElement);
};

const getIndentationDifference = (currentItem: listItem, previousItem?: listItem) => {
  return previousItem ? currentItem.indent - previousItem.indent : currentItem.indent - 1;
};

const findParentListAtLevel = (listElement: HTMLElement, indentationDifference: number) => {
  let parentList = null;
  let levelChange = 0;

  let iteratorList = listElement.parentElement;
  while (iteratorList) {
    if (iteratorList.tagName === 'UL' || iteratorList.tagName === 'OL') {
      levelChange++;
    }

    if (levelChange === indentationDifference) {
      parentList = iteratorList;
      break;
    }
    iteratorList = iteratorList.parentElement;
  }

  return parentList;
};
